import React, { useCallback, useMemo, useState } from 'react';
import { MessageContext, useChatUi } from '../ChatContext';
import MarkdownAnswer from './MarkdownAnswer';
import StatusLine from './StatusLine';
import SourcesPanel from './SourcesPanel';
import FigureGallery from './FigureGallery';
import FollowUps from './FollowUps';
import { fileUrl } from '../api/files';
import MessageActions from './MessageActions';

function Notice({ notice }) {
  if (!notice || !notice.text) return null;
  return (
    <p className={`cv-notice cv-notice--${notice.kind || 'info'}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
      </svg>
      <span>{notice.text}</span>
    </p>
  );
}

function ErrorBox({ error, onRetry, busy }) {
  const { t } = useChatUi();
  return (
    <div className="cv-error" role="alert">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
      </svg>
      <span className="cv-error__text">{error.message || t('Unable to send your message. Please try again.')}</span>
      {error.retryable && onRetry && (
        <button type="button" className="cv-btn cv-btn--small" onClick={onRetry} disabled={busy}>{t('Retry')}</button>
      )}
    </div>
  );
}

/**
 * One assistant turn: status while preparing, the markdown answer (streamed or final), notice, error with
 * Retry, figure gallery, sources strip, follow-ups (last message only) and per-message actions.
 * Keeps `.message.assistant > .message-width` (read by scripts/dtc-excel-batch.js).
 */
function AssistantMessage({ message, isLast, busyGlobal, onSend, onAppend, onRetry, onVote, vote }) {
  const { openViewer } = useChatUi();
  const [hoveredSourceId, setHoveredSourceId] = useState(null);
  const { text, status, busy, sources, sourceById, grounded, protocol, error } = message;

  const streaming = status === 'streaming' || (status === 'pending' && busy);
  const answerDone = status === 'complete' || status === 'stopped' || status === 'error';
  const figures = useMemo(() => sources.filter((s) => s.figure), [sources]);
  const showFigures = figures.length > 0 && (grounded !== false || message.figuresRequested || protocol < 2);
  const stripSources = useMemo(() => {
    if (protocol < 2) return [];
    const inGallery = new Set(showFigures ? figures.map((s) => s.id) : []);
    return sources.filter((s) => !inGallery.has(s.id));
  }, [protocol, showFigures, figures, sources]);
  const showSources = stripSources.length > 0 && grounded !== false;

  // a page opens in the page viewer; a chunk of the user's own file opens that file (a PDF at its page)
  const openSource = useCallback((id) => {
    const src = sourceById[id];
    if (src && src.kind === 'file') {
      window.open(fileUrl(src.file_path || src.file_id), '_blank', 'noopener');
      return;
    }
    openViewer(message.sources.filter((s) => s.kind !== 'file'), id);
  }, [openViewer, message.sources, sourceById]);
  const ctx = useMemo(() => ({
    message, sourceById, hoveredSourceId, setHoveredSourceId, openSource,
  }), [message, sourceById, hoveredSourceId, openSource]);

  return (
    <MessageContext.Provider value={ctx}>
      <div className={`message assistant cv-msg cv-msg--assistant is-${status}`} data-status={status}>
        <div className="cv-msg__body">
          <div className="message-width">
            {busy && !text && <StatusLine message={message} />}
            {text && (
              <MarkdownAnswer
                text={text}
                streaming={streaming && !answerDone}
                sourceById={sourceById}
              />
            )}
            {status === 'stopped' && <StoppedTag />}
            <Notice notice={message.notice} />
          </div>
          {error && <ErrorBox error={error} onRetry={() => onRetry(message.id)} busy={busyGlobal} />}
          {showFigures && <FigureGallery sources={figures} />}
          {showSources && <SourcesPanel message={{ ...message, sources: stripSources }} />}
          {busy && text && answerDone && <StatusLine message={message} compact />}
          {isLast && !busy && message.followups.length > 0 && (
            <FollowUps
              items={message.followups}
              prompt={message.followupsPrompt}
              onSend={onSend}
              onAppend={onAppend}
              disabled={busyGlobal}
            />
          )}
          {!busy && text && <MessageActions message={message} onVote={onVote} vote={vote} />}
        </div>
      </div>
    </MessageContext.Provider>
  );
}

function StoppedTag() {
  const { t } = useChatUi();
  return <span className="cv-tag">{t('Stopped')}</span>;
}

export default React.memo(AssistantMessage);
